
import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  MessageCircle, 
  Sparkles, 
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ContentConvertsSection = () => {
  const [selectedService, setSelectedService] = useState(null);
  
  const services = [
    {
      id: "roi",
      icon: TrendingUp,
      title: "Higher ROI",
      description: "Drive better returns with authentic creator content",
      dialogContent: {
        title: "How Higher ROI Works",
        steps: [
          "Campaign Brief Creation & Strategy",
          "Creator Selection & Matching",
          "Content Production & Review",
          "Post Analytics & ROI Tracking",
          "Performance Optimization"
        ]
      }
    },
    {
      id: "voice",
      icon: MessageCircle,
      title: "Authentic Voice",
      description: "Connect with audiences through genuine storytelling",
      dialogContent: {
        title: "Our Authenticity Process",
        steps: [
          "Creator Personality Assessment",
          "Brand Voice Alignment",
          "Creative Freedom Guidelines",
          "Content Review & Feedback",
          "Audience Engagement Analysis"
        ]
      }
    },
    {
      id: "quality",
      icon: Sparkles,
      title: "Quality Content",
      description: "Get professional-grade UGC that converts",
      dialogContent: {
        title: "Quality Content Creation",
        steps: [
          "Technical Requirements Setup",
          "Professional Equipment Check",
          "Content Guidelines & Training",
          "Multi-Stage Quality Review",
          "Performance Optimization"
        ]
      }
    }
  ];

  const trustedBrands = [
    { name: "Zomato", logo: "/images/zomato.png" },
    { name: "Plix", logo: "/images/plix.png" },
    { name: "Bombae", logo: "/images/bombeee.png" },
    { name: "The Ayurveda Co", logo: "/images/tac.png" },
    { name: "Perfora", logo: "/images/perfora.png" },
    { name: "Arata", logo: "/images/arata.png" },
    { name: "Koparo", logo: "/images/koparo.png" },
    { name: "Asaya", logo: "/images/asaya.png" },
    { name: "Bombay Shaving Company", logo: "/images/bombay.png" },
    { name: "Clove Dental", logo: "/images/clove.png" }
  ];

  return (
    <section id="content-converts" className="py-20 bg-gradient-to-br from-woost-soft-blue/20 via-white to-woost-soft-yellow/20">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div 
          className="text-center max-w-4xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 md:leading-tight lg:leading-tight">
            From awareness to conversions — Woost helps your brand grow with nano & micro influencers who deliver results
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <div key={service.id} className="bg-white p-6 rounded-lg shadow-sm">
                  <Icon className="w-8 h-8 text-woost-blue mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-16">
            <h3 className="text-2xl font-semibold mb-8">Trusted by Top Brands</h3>
            <div className="grid  gap-8 items-center justify-center" style={{gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))'}}>
              {trustedBrands.map((brand) => (
                <div key={brand.name} className="flex items-center justify-center">
                  <img 
                    src={brand.logo} 
                    alt={brand.name} 
                    className="h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
          
          <Button 
            className="bg-woost-yellow text-woost-black hover:bg-woost-yellow-light px-8 py-6 rounded-xl text-lg mx-auto mt-12"
            onClick={() => {
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Get Started with UGC Now <ArrowRight size={18} className="ml-2" />
          </Button>
        </motion.div>

        {services.map((service) => (
          <Dialog 
            key={service.id}
            open={selectedService === service.id}
            onOpenChange={(open) => {
              if (!open) setSelectedService(null);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{service.dialogContent.title}</DialogTitle>
                <DialogDescription>
                  <ul className="mt-4 space-y-3">
                    {service.dialogContent.steps.map((step, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-woost-soft-blue text-woost-blue flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </div>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        ))}
      </div>
    </section>
  );
};

export default ContentConvertsSection;
